import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { apiFetch } from "../services/api";
import { ToastContext } from "./ToastContext";

export const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [cartId, setCartId] = useState(() => {
    const saved = localStorage.getItem("cart_id");
    if (!saved || saved === "undefined" || saved === "null") {
      localStorage.removeItem("cart_id");
      return null;
    }
    return saved;
  });
  const [cartToken, setCartToken] = useState(() => {
    const saved = localStorage.getItem("cart_token");
    if (!saved || saved === "undefined" || saved === "null") {
      localStorage.removeItem("cart_token");
      return null;
    }
    return saved;
  });
  const [open, setOpen] = useState(false);
  const toast = useContext(ToastContext);

  const initCart = useCallback(async () => {
    try {
      const res = await apiFetch(
        "/cart",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ cart_id: cartId }),
        },
        { cartToken },
      );
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.cart_id || !data?.cart_token) {
        throw new Error(data?.error || "Unable to initialize basket");
      }
      setCartId(data.cart_id);
      setCartToken(data.cart_token);
      localStorage.setItem("cart_id", data.cart_id);
      localStorage.setItem("cart_token", data.cart_token);
      return data.cart_id;
    } catch {
      return null;
    }
  }, [cartId, cartToken]);

  const fetchCart = useCallback(
    async (id, token = cartToken) => {
      if (!id || id === "undefined" || id === "null") return;
      try {
        const res = await apiFetch(`/cart/${id}`, {}, { cartToken: token });
        if (!res.ok) {
          if (res.status === 403) {
            const newId = await initCart();
            const newToken = localStorage.getItem("cart_token");
            if (newId && newToken) {
              const retry = await apiFetch(
                `/cart/${newId}`,
                {},
                { cartToken: newToken },
              );
              if (retry.ok) setItems(await retry.json());
            }
          }
          return;
        }
        setItems(await res.json());
      } catch {
        setItems([]);
      }
    },
    [cartToken, initCart],
  );

  useEffect(() => {
    if (cartId) {
      fetchCart(cartId).catch(() => setItems([]));
      return;
    }
    initCart()
      .then((id) => {
        if (id) return fetchCart(id);
        return null;
      })
      .catch(() => setItems([]));
  }, [cartId, fetchCart, initCart]);

  const addItem = async (product_id, qty = 1) => {
    const safeQty = Number.isInteger(qty) && qty > 0 ? qty : 1;
    if (!product_id || Number(product_id) <= 0) {
      toast("Please select a valid product", "error");
      return;
    }
    const id = cartId || (await initCart());
    if (!id) {
      toast("Basket service is temporarily unavailable", "error");
      return;
    }
    const token = localStorage.getItem("cart_token") || cartToken;
    const res = await apiFetch(
      `/cart/${id}/items`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ product_id, qty: safeQty }),
      },
      { cartToken: token },
    );
    if (!res.ok) {
      toast("Could not add item right now", "error");
      return;
    }
    await fetchCart(id, token);
    toast("Added to basket 🌾");
    setOpen(true);
  };

  const updateItem = async (item_id, qty) => {
  if (!item_id || !cartId) return;

  const safeQty = Number.isInteger(qty)
    ? Math.max(0, qty)
    : 1;

  const token = localStorage.getItem("cart_token") || cartToken;

  const res = await apiFetch(
    `/cart/${cartId}/items/${item_id}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ qty: safeQty }),
    },
    { cartToken: token }
  );

  if (!res.ok) {
    toast("Unable to update basket item", "error");
    return;
  }

  await fetchCart(cartId, token);
};

  const refreshCart = useCallback(async () => {
    if (!cartId) return;
    await fetchCart(cartId);
  }, [cartId, fetchCart]);

  const total = items.reduce(
    (s, i) => s + (i.sale_price || i.price) * i.qty,
    0,
  );
  const count = items.reduce((s, i) => s + i.qty, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        updateItem,
        total,
        count,
        open,
        setOpen,
        cartId,
        cartToken,
        refreshCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}
